import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { API_BASE } from './api';
import { formatDateTime } from './formatDate';

const ICONS = { approved: '✓', rejected: '✕', removed: '⚑', comment: '✶', report: '⚑' };
const REFRESH_MS = 60 * 1000;

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"
      />
    </svg>
  );
}

const OPEN_OFFSET = 88; // px: swiped open, showing the Delete button
const DELETE_DISTANCE = 150; // px: a long swipe deletes straight away

// One notification that can be swiped left to delete (or, with a mouse, deleted
// with the × that appears on hover). A short swipe opens it to show Delete; a
// long swipe deletes it. Vertical scrolling still works (touch-action: pan-y).
function SwipeRow({ children, onOpenItem, onDelete, label }) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const start = useRef(null);
  const moved = useRef(false);

  const remove = () => {
    setLeaving(true);
    setOffset(-window.innerWidth);
    window.setTimeout(onDelete, 220);
  };

  const onTouchStart = (event) => {
    const touch = event.touches[0];
    start.current = { x: touch.clientX, y: touch.clientY, base: offset, horizontal: null };
    moved.current = false;
  };

  const onTouchMove = (event) => {
    if (!start.current) return;
    const touch = event.touches[0];
    const dx = touch.clientX - start.current.x;
    const dy = touch.clientY - start.current.y;
    if (start.current.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      start.current.horizontal = Math.abs(dx) > Math.abs(dy);
    }
    if (!start.current.horizontal) return;
    moved.current = true;
    setDragging(true);
    setOffset(Math.min(0, start.current.base + dx));
  };

  const onTouchEnd = () => {
    if (!start.current) return;
    start.current = null;
    setDragging(false);
    if (!moved.current) return;
    if (offset <= -DELETE_DISTANCE) remove();
    else setOffset(offset <= -OPEN_OFFSET / 2 ? -OPEN_OFFSET : 0);
  };

  const onClick = () => {
    // A swipe isn't a tap; tapping an open row closes it.
    if (moved.current) {
      moved.current = false;
      return;
    }
    if (offset !== 0) {
      setOffset(0);
      return;
    }
    onOpenItem();
  };

  return (
    <li className={`bell-row${leaving ? ' is-leaving' : ''}`}>
      <button
        type="button"
        className="bell-row-delete"
        onClick={remove}
        tabIndex={offset ? 0 : -1}
        aria-hidden={offset ? undefined : 'true'}
      >
        Delete
      </button>
      <div
        className="bell-row-front"
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? 'none' : 'transform 0.22s ease',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {children(onClick)}
        <button type="button" className="bell-item-remove" onClick={remove} aria-label={`Delete notification: ${label}`}>
          ×
        </button>
      </div>
    </li>
  );
}

// Footer bell: shows unread count; opens a frosted orange panel that slides up
// with the person's in-app notifications.
export default function NotificationBell() {
  const { user, token } = useAuth() || {};
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [footerHeight, setFooterHeight] = useState(110);

  const signedIn = Boolean(user && token);

  const load = useCallback(async () => {
    if (!signedIn) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setItems(Array.isArray(data.notifications) ? data.notifications : []);
        setUnread(Number(data.unread || 0));
      }
    } catch (error) {
      // Offline: keep showing what we had.
    } finally {
      setLoading(false);
    }
  }, [signedIn, token]);

  useEffect(() => {
    if (!signedIn) {
      setItems([]);
      setUnread(0);
      setOpen(false);
      return undefined;
    }
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [signedIn, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const markRead = async (ids) => {
    if (!signedIn) return;
    await fetch(`${API_BASE}/api/notifications/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(ids ? { ids } : {}),
    }).catch(() => {});
  };

  const toggle = () => {
    const footer = document.querySelector('footer');
    if (footer) setFooterHeight(footer.offsetHeight);
    if (!open) load();
    setOpen((value) => !value);
  };

  const openItem = (item) => {
    if (!item.read) {
      setItems((list) => list.map((entry) => (entry.id === item.id ? { ...entry, read: true } : entry)));
      setUnread((count) => Math.max(0, count - 1));
      markRead([item.id]);
    }
    setOpen(false);
    // Reports go to the admin page, where they can be dismissed or acted on.
    if (item.type === 'report') history.push('/admin');
    else if (item.eventSlug) history.push(`/event/${item.eventSlug}`);
  };

  const deleteItem = async (item) => {
    setItems((list) => list.filter((entry) => entry.id !== item.id));
    if (!item.read) setUnread((count) => Math.max(0, count - 1));
    try {
      const res = await fetch(`${API_BASE}/api/notifications/${encodeURIComponent(item.id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 404) load();
    } catch (error) {
      load(); // Offline: put it back.
    }
  };

  const markAllRead = () => {
    setItems((list) => list.map((entry) => ({ ...entry, read: true })));
    setUnread(0);
    markRead(null);
  };

  // Notifications belong to an account, so the bell only shows once signed in.
  if (!signedIn) return null;

  return (
    <>
      <button
        type="button"
        className="bell-button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <BellIcon />
        {unread > 0 && <span className="bell-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <div className="bell-backdrop" onClick={() => setOpen(false)} role="presentation" />
          <section
            className="bell-panel"
            style={{ bottom: footerHeight + 8 }}
            role="dialog"
            aria-label="Notifications"
          >
            <header className="bell-header">
              <h2>Notifications</h2>
              {unread > 0 && (
                <button type="button" onClick={markAllRead}>Mark all read</button>
              )}
            </header>

            {items.length === 0 ? (
              <div className="bell-empty">
                <p>{loading ? 'Loading…' : 'No notifications yet.'}</p>
              </div>
            ) : (
              <ul className="bell-list">
                {items.map((item) => (
                  <SwipeRow
                    key={item.id}
                    label={item.message || item.eventTitle || 'notification'}
                    onOpenItem={() => openItem(item)}
                    onDelete={() => deleteItem(item)}
                  >
                    {(onClick) => (
                    <button
                      type="button"
                      className={`bell-item${item.read ? '' : ' is-unread'}`}
                      onClick={onClick}
                    >
                      <span className={`bell-icon is-${item.type}`} aria-hidden="true">{ICONS[item.type] || '✶'}</span>
                      <span className="bell-text">
                        <strong>{item.eventTitle || 'Your event'}</strong>
                        <span>{item.message}</span>
                        <small>{formatDateTime(item.createdAt)}</small>
                      </span>
                      {!item.read && <span className="bell-dot" aria-label="Unread" />}
                    </button>
                    )}
                  </SwipeRow>
                ))}
              </ul>
            )}
          </section>
        </>,
        document.body
      )}
    </>
  );
}
